import { useCallback, useEffect, useRef, useState } from 'react';
import { io, type Socket } from 'socket.io-client';

import { env } from '@/env';
import { qgammonAuthHeaders, qgammonSocketAuth } from '@/lib/qgammon/auth';
import { QGAMMON_SOCKET_OPTIONS } from '@/lib/qgammon/socketOptions';
import { ServerEvents, type InviteAcceptedPayload } from '@qlashgeminiz/qgammon-types';
import type { Player } from '@qlashgeminiz/qgammon-rules';

export type InviteStatus = 'idle' | 'creating' | 'pending' | 'accepted';

/** What `createInvite` resolves to / the pending invite the sheet renders. */
export interface PendingInvite {
  code: string;
  /** ISO timestamp at which the invite expires (for the countdown). */
  expiresAt: string;
  matchLength: 1 | 3 | 5;
}

/**
 * The creator-side resolution: the freshly created match + the CREATOR's colour
 * (the `invite:accepted` push carries the creator's side) + the acceptor info.
 */
export interface InviteAccepted {
  matchId: string;
  /** The CREATOR's colour — used as `state.perspective` on navigation. */
  color: Player;
  opponent: InviteAcceptedPayload['opponent'];
}

export interface InviteController {
  status: InviteStatus;
  /** The live invite once created (code + expiry), else null. */
  pending: PendingInvite | null;
  /** Set when the creator's socket receives `invite:accepted`. */
  accepted: InviteAccepted | null;
  error: 'limit_reached' | 'create_failed' | null;
  /** POST /invites → stash {code, expiresAt} and open the listening socket. */
  createInvite: (matchLength: 1 | 3 | 5) => void;
  /** DELETE /invites/:code (creator only) and reset to idle. */
  cancelInvite: (code: string) => void;
}

/**
 * Drives the CREATOR side of a challenge-by-consent invite from the TMA.
 *
 * Lifecycle (mirrors `useRankedQueue`): the listening Socket.IO connection is
 * opened ONLY while an invite is pending. `createInvite` POSTs `/invites`, and
 * on success opens a handshake-auth-only socket (no `match:join`) — the
 * `invite:accepted` event is fanned out by the gameserver to every one of the
 * creator's sockets (UserSocketIndex), so a listen-only socket receives it.
 * When it arrives we stash `accepted` and let the CALLER navigate to the match
 * with the creator's colour. `cancelInvite` DELETEs the code and tears the
 * socket down. The socket also closes on accept and on unmount.
 *
 * The invite endpoints live on the gameserver (`env.qgammonWsUrl`, :4001) and
 * use the same handshake/header auth as the ranked queue (centralised in
 * `@/lib/qgammon/auth`): a per-user Logto Bearer when signed in, else the
 * dev-bypass fallback. Per-user identity is what makes the invite creator and
 * acceptor distinct users instead of both being the shared "Dev" id.
 */
export function useInvite(): InviteController {
  const socketRef = useRef<Socket | null>(null);
  const [status, setStatus] = useState<InviteStatus>('idle');
  const [pending, setPending] = useState<PendingInvite | null>(null);
  const [accepted, setAccepted] = useState<InviteAccepted | null>(null);
  const [error, setError] = useState<InviteController['error']>(null);

  // Set on unmount so an in-flight `qgammonSocketAuth()` resolving after the
  // component is gone doesn't create (and leak) a socket the cleanup can't reach.
  const unmountedRef = useRef(false);

  // Tear the socket down once on unmount / when no invite is live.
  const closeSocket = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      closeSocket();
    };
  }, [closeSocket]);

  const openSocket = useCallback(() => {
    if (socketRef.current) return;
    // Handshake auth resolves asynchronously (may mint/refresh a Logto token).
    void qgammonSocketAuth().then((auth) => {
      // A concurrent openSocket / a cancel that already opened-or-closed the
      // socket may have won the race, or we unmounted mid-flight; bail in either
      // case so we never leak a socket past unmount.
      if (socketRef.current || unmountedRef.current) return;
      const socket = io(env.qgammonWsUrl, { ...QGAMMON_SOCKET_OPTIONS, auth });
      socketRef.current = socket;

      socket.on(ServerEvents.InviteAccepted, (p: InviteAcceptedPayload) => {
        setAccepted({ matchId: p.matchId, color: p.color, opponent: p.opponent });
        setStatus('accepted');
        // The invite is consumed — stop listening; the caller navigates next.
        closeSocket();
      });
    });
  }, [closeSocket]);

  const createInvite = useCallback(
    (matchLength: 1 | 3 | 5) => {
      setError(null);
      setAccepted(null);
      setStatus('creating');
      void (async () => {
        try {
          // Logto Bearer if signed in, else dev-bypass headers (dev only).
          const headers = await qgammonAuthHeaders(true);
          const res = await fetch(`${env.qgammonWsUrl}/invites`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ matchLength }),
          });
          if (res.status === 409) {
            setError('limit_reached');
            setStatus('idle');
            return;
          }
          if (!res.ok) throw new Error(`create failed: ${res.status}`);
          const body = (await res.json()) as {
            code: string;
            matchLength: 1 | 3 | 5;
            expiresAt: string;
          };
          setPending({ code: body.code, expiresAt: body.expiresAt, matchLength: body.matchLength });
          setStatus('pending');
          openSocket();
        } catch {
          setError('create_failed');
          setStatus('idle');
        }
      })();
    },
    [openSocket],
  );

  const cancelInvite = useCallback(
    (code: string) => {
      setStatus('idle');
      setPending(null);
      setAccepted(null);
      setError(null);
      closeSocket();
      void (async () => {
        try {
          const headers = await qgammonAuthHeaders();
          await fetch(`${env.qgammonWsUrl}/invites/${encodeURIComponent(code)}`, {
            method: 'DELETE',
            headers,
          });
        } catch {
          /* best-effort */
        }
      })();
    },
    [closeSocket],
  );

  return { status, pending, accepted, error, createInvite, cancelInvite };
}
